import { Suspense, lazy, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import AppShell from '../components/AppShell';
import RouteTitle from '../components/RouteTitle';
import SupportFeedbackWidget from '../components/SupportFeedbackWidget';
import { ChalkboardModeAutoProvider } from '../styles/chalkboardMode';

// Auto-reload on stale chunks after deploy (MIME type error = server returned HTML for missing .js)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry(
  factory: () => Promise<{ default: ComponentType<any> }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('dynamically imported module') || msg.includes('Failed to fetch')) {
        // Prevent reload loop: only reload once per session
        const key = 'aot_chunk_reload';
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          window.location.reload();
        }
      }
      throw err;
    }),
  );
}

// New Navigation Architecture pages
/**
 * Redirect an old address to its one current name, carrying the route params and the query
 * string (floor plan, 11.9.2026: /session/:code → /join/:code, /public-research/:id → /study/:id).
 */
function ParamRedirect({ to }: { to: string }) {
    const params = useParams();
    const location = useLocation();
    const target = to.replace(/:([A-Za-z]+)/g, (_m, key: string) => encodeURIComponent(params[key] ?? ''));
    return <Navigate to={`${target}${location.search}`} replace />;
}

const DataOwnershipPage = lazyWithRetry(() => import('./DataOwnershipPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./TermsOfServicePage'));
const SettingsPage = lazyWithRetry(() => import('./SettingsPage'));
const ResearchInfoPage = lazyWithRetry(() => import('./ResearchInfoPage'));
const ResearchParticipationPage = lazyWithRetry(() => import('./ResearchParticipationPage'));
const WhitepaperPage = lazyWithRetry(() => import('./WhitepaperPage'));
const ProtocolPage = lazyWithRetry(() => import('./ProtocolPage'));
const AdhdResearchPage = lazyWithRetry(() => import('./AdhdResearchPage'));
const ResearchLandingPage = lazyWithRetry(() => import('./ResearchLandingPage'));
const MetricsDashboard = lazyWithRetry(() => import('./MetricsDashboard'));
const StudySciencePage = lazyWithRetry(() => import('./StudySciencePage'));
const StudyScienceReportPage = lazyWithRetry(() => import('./StudyScienceReportPage'));
const BenchmarkPage = lazyWithRetry(() => import('./BenchmarkPage'));
const PublicResearchPage = lazyWithRetry(() => import('./PublicResearchPage'));


const HostExperiencePage = lazyWithRetry(() => import('./HostExperiencePage'));
const HostParticipantPreviewPage = lazyWithRetry(() => import('./HostParticipantPreviewPage'));
const HostSessionsPage = lazyWithRetry(() => import('./HostSessionsPage'));
const HostStudyPage = lazyWithRetry(() => import('./HostStudyPage'));
const StudyJoinPage = lazyWithRetry(() => import('./StudyJoinPage'));
const ParticipantStudyPage = lazyWithRetry(() => import('./ParticipantStudyPage'));
const CreditsPage = lazyWithRetry(() => import('./CreditsPage'));
const CreateStudyPage = lazyWithRetry(() => import('./CreateStudyPage'));
const CreateSessionPage = lazyWithRetry(() => import('./CreateSessionPage'));
const HostProgrammeJourneyPage = lazyWithRetry(() => import('./HostProgrammeJourneyPage'));
const HostProgrammeStoryPage = lazyWithRetry(() => import('./HostProgrammeStoryPage'));
const HostStoryHallPage = lazyWithRetry(() => import('./HostStoryHallPage'));
const PresentedStoryPage = lazyWithRetry(() => import('./PresentedStoryPage'));
const HostStoryPresenterPage = lazyWithRetry(() => import('./HostStoryHallPage').then((m) => ({ default: m.HostStoryPresenterPage })));
const HostWorkPage = lazyWithRetry(() => import('./HostWorkPage'));
const ParticipantProgrammePage = lazyWithRetry(() => import('./ParticipantProgrammePage'));
const ParticipantJourneyPage = lazyWithRetry(() => import('./ParticipantJourneyPage'));
const CustomerJourneyPage = lazyWithRetry(() => import('./CustomerJourneyPage'));
const ProgrammeFacilitatePage = lazyWithRetry(() => import('./ProgrammeFacilitatePage'));
const ClientStoryPage = lazyWithRetry(() => import('./ClientStoryPage'));
const JournalPage = lazyWithRetry(() => import('./JournalPage'));
const DevCadenceParticipantPage = lazyWithRetry(() => import('./DevCadenceParticipantPage'));
const DevGroupStoryPage = lazyWithRetry(() => import('./DevGroupStoryPage'));
const DevHostHomePreview = lazyWithRetry(() => import('./DevHostHomePreview'));
const DevHostLandingPreview = lazyWithRetry(() => import('./DevHostLandingPreview'));
const DevExerciseHomePreview = lazyWithRetry(() => import('./DevExerciseHomePreview'));
const DevMeasurementFlowPreview = lazyWithRetry(() => import('./DevMeasurementFlowPreview'));
const HostExerciseHomePage = lazyWithRetry(() => import('./HostExerciseHomePage'));
const HostExerciseInvitePage = lazyWithRetry(() => import('./HostExerciseInvitePage'));
const HostExerciseSpeakPage = lazyWithRetry(() => import('./HostExerciseSpeakPage'));
const HostExerciseTunePage = lazyWithRetry(() => import('./HostExerciseTunePage'));
const SessionEntryPage = lazyWithRetry(() => import('./SessionEntryPage'));
const PublicFormatSummaryPage = lazyWithRetry(() => import('./PublicFormatSummaryPage'));
// Spinagate (Spinacor ELY project, TP2): hidden behind an access code until the partner launches it.
const SpinagateStartPage = lazyWithRetry(() => import('./spinagate/SpinagateStartPage'));
const SpinagateDonePage = lazyWithRetry(() => import('./spinagate/SpinagateDonePage'));
const SpinagateLinkPage = lazyWithRetry(() => import('./spinagate/SpinagateLinkPage'));
const PerResponseV2Page = lazyWithRetry(() => import('./PerResponseV2Page'));
const EmbedPreviewPage = lazyWithRetry(() => import('./EmbedPreviewPage'));
const ForbiddenPage = lazyWithRetry(() => import('./ForbiddenPage'));
const DashboardPage = lazyWithRetry(() => import('./DashboardPage'));
const AdminDashboard = lazyWithRetry(() => import('./AdminDashboard'));
const SuperAdminDashboard = lazyWithRetry(() => import('./SuperAdminDashboard'));
const FounderCockpit = lazyWithRetry(() => import('./FounderCockpit'));
const PromptManagerPage = lazyWithRetry(() => import('./PromptManagerPage'));
const SignInPage = lazyWithRetry(() => import('./SignInPage'));
const SubscriptionSuccessPage = lazyWithRetry(() => import('./SubscriptionSuccessPage'));
const SubscriptionCancelledPage = lazyWithRetry(() => import('./SubscriptionCancelledPage'));
const StartExercisePage = lazyWithRetry(() => import('./StartExercisePage'));
const StartGoPage = lazyWithRetry(() => import('./StartGoPage'));
const PreviewSessionIntroPage = lazyWithRetry(() => import('./PreviewSessionIntroPage'));
const PreviewSessionEndPage = lazyWithRetry(() => import('./PreviewSessionEndPage'));
const PreviewAnswersDeckPage = lazyWithRetry(() => import('./PreviewAnswersDeckPage'));
const PreviewPaymentPanelPage = lazyWithRetry(() => import('./PreviewPaymentPanelPage'));
const PreviewEntityCheckPage = lazyWithRetry(() => import('./PreviewEntityCheckPage'));
const PreviewAlignTabPage = lazyWithRetry(() => import('./PreviewAlignTabPage'));
const PreviewOrgNamePage = lazyWithRetry(() => import('./PreviewOrgNamePage'));
const PreviewSuggestedTermsPage = lazyWithRetry(() => import('./PreviewSuggestedTermsPage'));

// Phase A preview — chalkboard / paper design preview, dev-only routes (#511)
const PreviewHomePage = lazyWithRetry(() => import('./PreviewHomePage'));
const PreviewPricingPage = lazyWithRetry(() => import('./PreviewPricingPage'));
const PreviewSciencePage = lazyWithRetry(() => import('./PreviewSciencePage'));
const PreviewAboutPage = lazyWithRetry(() => import('./PreviewAboutPage'));
const PaperNotFoundPage = lazyWithRetry(() => import('./PaperNotFoundPage'));

const App = () => (
    <BrowserRouter>
      <RouteTitle />
      <ChalkboardModeAutoProvider>
        <AppShell>
            <Suspense
                fallback={(
                    <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400">
                        Loading experience…
                    </div>
                )}
            >
                <Routes>
                {/* Front page = chalkboard/paper homepage (#511 Phase A → live 2026-06-10). */}
                <Route path="/" element={<PreviewHomePage />} />
                {/* Clean alias for the chalkboard science page (nav links use this).
                    Pricing is intentionally NOT linked or aliased (Kalle 2026-06-10:
                    host access works via email — simplify, no public pricing page). */}
                <Route path="/science" element={<PreviewSciencePage />} />
                {/* #573/#575 dev previews — visual review screens, dev builds only (#653/#735) */}
                {import.meta.env.DEV && (
                    <Route path="/preview/session-intro" element={<PreviewSessionIntroPage />} />
                )}
                {import.meta.env.DEV && (
                    <Route path="/preview/session-end" element={<PreviewSessionEndPage />} />
                )}
                {/* #906 answers slide deck — visual review, dev builds only */}
                {import.meta.env.DEV && (
                    <Route path="/preview/answers-deck" element={<PreviewAnswersDeckPage />} />
                )}
                {/* #931 continuation sales moment — visual review, dev builds only */}
                {import.meta.env.DEV && (
                    <Route path="/preview/payment-panel" element={<PreviewPaymentPanelPage />} />
                )}
                {/* #1015 entity spelling check — visual review, dev builds only */}
                {import.meta.env.DEV && (
                    <Route path="/preview/entity-check" element={<PreviewEntityCheckPage />} />
                )}
                {/* #1022 Align your AI five doors — visual review, dev builds only */}
                {import.meta.env.DEV && (
                    <Route path="/preview/align-tab" element={<PreviewAlignTabPage />} />
                )}
                {/* #1026 organization fields in the intake — visual review, dev builds only */}
                {import.meta.env.DEV && (
                    <Route path="/preview/org-name" element={<PreviewOrgNamePage />} />
                )}
                {/* #1027 suggested terms in the tune step — visual review, dev builds only */}
                {import.meta.env.DEV && (
                    <Route path="/preview/suggested-terms" element={<PreviewSuggestedTermsPage />} />
                )}
                {/* #511 Phase A preview aliases — old links redirect to the canonical paths (#735) */}
                <Route path="/preview/homepage" element={<Navigate to="/" replace />} />
                {/* No public pricing surface (Kalle 2026-07-03) — same rule as /pricing below;
                    the preview keeps working in dev builds for design review. */}
                <Route
                    path="/preview/pricing"
                    element={import.meta.env.DEV ? <PreviewPricingPage /> : <Navigate to="/shared-language" replace />}
                />
                <Route path="/preview/science" element={<Navigate to="/science" replace />} />
                <Route path="/preview/about" element={<Navigate to="/about" replace />} />
                <Route path="/research" element={<ResearchLandingPage />} />
                <Route path="/research/adhd" element={<AdhdResearchPage />} />
                {/* Meet Your Mind advertised the retired daily-word-reflection format — removed (#735) */}
                <Route path="/research/meet-your-mind" element={<Navigate to="/research" replace />} />
                <Route path="/get-started" element={<Navigate to="/shared-language" replace />} />
                {/* Canonical About = chalkboard version (real copy 2026-06-10). */}
                <Route path="/about" element={<PreviewAboutPage />} />
                <Route path="/about-legacy" element={<Navigate to="/about" replace />} />
                <Route path="/data-ownership" element={<DataOwnershipPage />} />
                <Route path="/research-info" element={<ResearchInfoPage />} />
                <Route path="/research-participation" element={<ResearchParticipationPage />} />
                <Route path="/whitepaper" element={<WhitepaperPage />} />
                {/* AOT Analysis Protocol — long-form readable page (chalkboard light). Linked from the
                    footer and /shared-language; restored after the 11.9. route cuts (#1251). */}
                <Route path="/protocol" element={<ProtocolPage />} />
                {/* /public (old feed) superseded by the /public-research archive. */}
                <Route path="/public" element={<Navigate to="/public-research" replace />} />
                <Route path="/public-research" element={<PublicResearchPage />} />
                <Route path="/public-research/:studyId" element={<ParamRedirect to="/study/:studyId" />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/signin" element={<SignInPage />} />
                {/* No public pricing until real prices are set (Kalle 2026-07-03);
                    prices will be presented on /start when locked. */}
                <Route path="/pricing" element={<Navigate to="/shared-language" replace />} />
                <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                <Route path="/subscription/cancelled" element={<SubscriptionCancelledPage />} />
                {/* The public offer lives at /shared-language (Kalle 2026-07-27), superseding
                    "/start = THE front door" (2026-07-03, GH #691). That decision was made when
                    /start's job was ROUTING — sorting visitors into format names. Those names are
                    gone from the public path, so there is nothing left to route: naming the page
                    after the asset it builds beats naming it after an action of ours.
                    /start stays alive with its original job for people already inside — it is
                    where the host's "+ New exercise" lands. */}
                <Route path="/become-a-host" element={<Navigate to="/shared-language" replace />} />
                <Route path="/shared-language" element={<StartExercisePage variant="offer" />} />
                {/* #668 guided exercise-definition wizard — the host's own picker */}
                <Route path="/start" element={<StartExercisePage />} />
                {/* #669 wizard continuation: sign-in + free host tier, then forward to the chosen creation flow */}
                <Route path="/start/go" element={<StartGoPage />} />

                {/* Legacy routes — pages removed (#735), old links redirect to their replacements */}
                <Route path="/legacy" element={<Navigate to="/" replace />} />
                <Route path="/forbidden" element={<ForbiddenPage />} />
                <Route path="/signup/coach" element={<Navigate to="/shared-language" replace />} />
                <Route path="/signup/participant" element={<Navigate to="/shared-language" replace />} />
                <Route path="/explore" element={<Navigate to="/public-research" replace />} />
                {/* Fabricated methodology page removed (#578) — the whitepaper is the accurate public methodology */}
                <Route path="/formats/documentation" element={<Navigate to="/whitepaper" replace />} />
                {/* Bare /host is no longer a format picker — /start is the one front door
                    (#673, locked 2026-07-03). Land hosts on their workspace, same as
                    /host/dashboard. HostExperiencePage now only runs live sessions below. */}
                <Route path="/host" element={<Navigate to="/host/home" replace />} />
                {/* Orphan duplicate of Host HQ removed (#735) — old links land on the HQ */}
                <Route path="/host/dashboard" element={<Navigate to="/host/home" replace />} />
                <Route path="/host/credits" element={<CreditsPage />} />
                {/* Emergent Cadence (2026-07-23): programmes grow from sessions — the standalone creation flow is retired. */}
                <Route path="/host/create-programme" element={<Navigate to="/host/create-session" replace />} />
                <Route path="/host/create-study" element={<CreateStudyPage />} />
                {/* Setting up your first reflection is not host administration — the guided
                    entry path keeps it under /start so the address matches the experience
                    (Kalle 2026-07-27). /host/create-session stays for existing links. */}
                {/* One name for the define page (floor plan 11.9.): the funnel lands on the host address. */}
                <Route path="/start/define" element={<Navigate to="/host/create-session" replace />} />
                <Route path="/host/create-session" element={<CreateSessionPage />} />
                {/* Old standalone scheduling page folded into the journey cockpit (#630) — redirect. */}
                <Route path="/host/programmes/:programmeId/events" element={<Navigate to=".." relative="path" replace />} />
                <Route path="/host/programmes/:programmeId" element={<HostProgrammeJourneyPage />} />
                {/* #1560: accepting a facilitator invitation, and the owner's client view while a facilitator runs it. */}
                <Route path="/host/programmes/:programmeId/facilitate" element={<ProgrammeFacilitatePage />} />
                <Route path="/host/programmes/:programmeId/customer" element={<CustomerJourneyPage />} />
                <Route path="/host/programmes/:programmeId/customer/story" element={<ClientStoryPage />} />
                {/* #1364: the programme's group story; its routes answer only hosts it is open to (GROUP_STORY_HOSTS). */}
                <Route path="/host/programmes/:programmeId/story" element={<HostProgrammeStoryPage />} />
                {/* #1364 plan phase 6: the lecture hall on the host's machine, and its presenter window. */}
                <Route path="/host/programmes/:programmeId/story/hall" element={<HostStoryHallPage />} />
                <Route path="/host/programmes/:programmeId/story/present" element={<HostStoryPresenterPage />} />
                <Route path="/host/sessions/:sessionId/story/hall" element={<HostStoryHallPage />} />
                <Route path="/host/sessions/:sessionId/story/present" element={<HostStoryPresenterPage />} />
                {/* #1364 decisions 23, 25: a lecture hall link on the hall's own computer, no sign-in. */}
                <Route path="/present/:code" element={<PresentedStoryPage />} />
                {/* "Your work" landing (Host Home canvas) — becomes the host default once approved. */}
                <Route path="/host/home" element={
                    <ProtectedRoute requiredRole="HOST">
                        <HostWorkPage />
                    </ProtectedRoute>
                } />
                <Route path="/host/sessions" element={<HostSessionsPage />} />
                <Route path="/host/sessions/:sessionId" element={<HostExperiencePage />} />
                {/* Kalle 12.10.2026: "View as participant", the participant page before or after sharing. */}
                <Route path="/host/sessions/:sessionId/participant-view" element={<HostParticipantPreviewPage />} />
                {/* The standalone exercise's home (R1 wiring): the session page above stays the reading. */}
                <Route path="/host/exercises/:sessionId" element={<HostExerciseHomePage />} />
                {/* The home's doors: speaking your round and inviting are flows OF the home,
                    not sections of the reading page (Kalle 27.8.). */}
                <Route path="/host/exercises/:sessionId/invite" element={<HostExerciseInvitePage />} />
                <Route path="/host/exercises/:sessionId/speak" element={<HostExerciseSpeakPage />} />
                <Route path="/host/exercises/:sessionId/tune" element={<HostExerciseTunePage />} />
                <Route path="/host/studies/:studyId" element={<HostStudyPage />} />
                {/* Old participant address (floor plan 11.9.): one name, /join, for the exercise entry. */}
                <Route path="/session/:shareCode" element={<ParamRedirect to="/join/:shareCode" />} />
                <Route path="/join/:shareCode?" element={<SessionEntryPage />} />
                <Route path="/join/study/:shareCode" element={<StudyJoinPage />} />
                <Route path="/spinagate" element={<SpinagateStartPage />} />
                <Route path="/spinagate/done/:sessionId" element={<SpinagateDonePage />} />
                <Route path="/spinagate/r/:code" element={<SpinagateLinkPage />} />
                <Route path="/study/:studyId" element={<ParticipantStudyPage />} />
                <Route path="/study/:studyId/science" element={<StudySciencePage />} />
                <Route path="/study/:studyId/science/report" element={<StudyScienceReportPage />} />

                {/* Vanity URL: /research/organic-vs-artificial → future human+AI study (placeholder → research page) */}
                <Route path="/research/organic-vs-artificial" element={<Navigate to="/research" replace />} />
                <Route path="/research/organic-vs-artificial/science" element={<Navigate to="/research" replace />} />
                <Route path="/summary/:shareCode" element={<PublicFormatSummaryPage />} />
                {/* Programme-level participant journey via magic link — the journeyCode in the
                    URL is the credential (no login). Backend returns participant-safe data only. */}
                <Route path="/journey/:journeyCode" element={<ParticipantJourneyPage />} />
                {/* Customer (client) reader view via programme-level customer link (#1087). */}
                <Route path="/client/:code" element={<CustomerJourneyPage />} />
                {/* #1364 plan phase 7: the client's group story (published, shared rounds only). */}
                <Route path="/client/:code/story" element={<ClientStoryPage />} />
                {/* Learning journal (#1162): personal notes for hosts and customers; signs in on the page itself. */}
                <Route path="/journal" element={<JournalPage />} />
                {/* PR A.15 cleanup: deprecated /synthesis route now redirects to /summary/:shareCode.
                    The old <ParticipantSynthesisPage> + <PublicQuestionSynthesis> components
                    were marked DEPRECATED and rendered the same content the canonical
                    /summary/:shareCode route now produces via SessionSummaryView /
                    StudyDaySummaryView. Removed in this PR. */}
                <Route path="/summary/:shareCode/synthesis" element={<Navigate to=".." relative="path" replace />} />
                {/* /per-response uses the V2 redesign (chalkboard look, accordion, fingerprint).
                    V1 rollback page removed (#735); old v1/v2 links redirect to the canonical path. */}
                <Route path="/sessions/:sessionId/per-response" element={<PerResponseV2Page />} />
                <Route path="/sessions/:sessionId/per-response-v2" element={<Navigate to="../per-response" relative="path" replace />} />
                <Route path="/sessions/:sessionId/per-response-v1" element={<Navigate to="../per-response" relative="path" replace />} />
                <Route path="/embed/:shareCode" element={<EmbedPreviewPage />} />
                
                {/* Dashboard Routes - Protected */}
                <Route path="/dashboard" element={
                    <ProtectedRoute requiredRole="PARTICIPANT">
                        <DashboardPage />
                    </ProtectedRoute>
                } />
                <Route path="/programmes/:programmeId" element={
                    <ProtectedRoute requiredRole="PARTICIPANT">
                        <ParticipantProgrammePage />
                    </ProtectedRoute>
                } />
                {import.meta.env.DEV && (
                    <Route path="/dev/cadence-participant" element={<DevCadenceParticipantPage />} />
                )}
                {import.meta.env.DEV && (
                    <Route path="/dev/group-story" element={<DevGroupStoryPage />} />
                )}
                {import.meta.env.DEV && (
                    <Route path="/dev/host-home" element={<DevHostHomePreview />} />
                )}
                {import.meta.env.DEV && (
                    <Route path="/dev/host-landing" element={<DevHostLandingPreview />} />
                )}
                {import.meta.env.DEV && (
                    <Route path="/dev/exercise-home" element={<DevExerciseHomePreview />} />
                )}
                {import.meta.env.DEV && (
                    <Route path="/dev/measurement-flow" element={<DevMeasurementFlowPreview />} />
                )}
                <Route path="/admin" element={
                    <ProtectedRoute requiredRole="ADMIN">
                        <AdminDashboard />
                    </ProtectedRoute>
                } />
                <Route path="/superadmin" element={
                    <ProtectedRoute requiredRole="SUPERADMIN">
                        <SuperAdminDashboard />
                    </ProtectedRoute>
                } />
                <Route path="/founder" element={
                    <ProtectedRoute requiredRole="SUPERADMIN">
                        <FounderCockpit />
                    </ProtectedRoute>
                } />
                <Route path="/benchmark" element={
                    <ProtectedRoute requiredRole="ADMIN">
                        <BenchmarkPage />
                    </ProtectedRoute>
                } />
                <Route path="/metrics" element={
                    <ProtectedRoute requiredRole="SUPERADMIN">
                        <MetricsDashboard />
                    </ProtectedRoute>
                } />
                <Route path="/prompts/manager" element={
                    <ProtectedRoute requiredRole="SUPERADMIN">
                        <PromptManagerPage />
                    </ProtectedRoute>
                } />
                
                {/* Wildcard 404 — paper-mode not-found page (replaces the
                    silent redirect-to-home that hid mistyped URLs). */}
                <Route path="*" element={<PaperNotFoundPage />} />
                    </Routes>
                </Suspense>
                {/* Floating route into the OM Asiakasdeski. Decides its own
                    visibility: host views and the participant journey, never
                    while a participant is answering. */}
                <SupportFeedbackWidget />
            </AppShell>
      </ChalkboardModeAutoProvider>
        </BrowserRouter>
);

export default App;